import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  AlertDialogHeader,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { getItem, setItem } from "@/lib/storage-utils";
import { APP_ALERT_MODULE, QueryKeys, STORAGE, STORAGE_KEY } from "@/constants";
import type { AppAlertDialogStatus } from "@/types";
import useUserInfo from "@/hooks/use-user-info";
import type { PaperlessEobFetchResponse } from "@/model/paperlessEob";
import { PAPARLESS_EOB_UPDATE_ACTION } from "@/model/paperlessEob";
import { queryClient } from "@/hooks/use-query-client";
import type { IUiLabelsResponse } from "@/model/ui-labels";
import { usePaperlessEob } from "@/hooks/use-paperlessEob";
import {
  Ban,
  Bell,
  CircleAlert,
  CircleCheck,
  CircleX,
  Leaf,
  Loader2,
} from "lucide-react";
import PaperlessEobResultDialog from "./PaperlessEobResultDialog";
import { DEFAULT_PAPERLESS_EOB_VALUES } from "@/constants/ui_labels";
import {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import IDENTIFIERS from "@/constants/test_id";
import { lowerCaseComparison } from "@/lib/string-util";
import { AuthMode } from "@/context/auth-provider";
import { UDPATE_PAPERLESS_EOB_MESSAGE } from "@/constants/paperless-eob";
import type { PaperlessEobMessage } from "@/types/paperless-eob";

const PaperlessEobAlert = () => {
  const paperlessEobMessage: PaperlessEobMessage = {
    title: UDPATE_PAPERLESS_EOB_MESSAGE.GENERIC_FAILURE.TITLE,
    body: UDPATE_PAPERLESS_EOB_MESSAGE.GENERIC_FAILURE.MESSAGE,
    confirmBtnLabel: "OK",
    confirmBtnStatus: false,
    icon: CircleAlert,
    iconClassName: "text-red-700 bg-red-100",
  };
  const [successAlert, setSuccessAlert] = useState(paperlessEobMessage);
  const userInfo = useUserInfo();
  const { mode } = getItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION);
  const onGuestMode = lowerCaseComparison(mode, AuthMode.GUEST);
  const {
    updatePaperlessEob,
    isGoPaperlessUpdating,
    dontAskAgainPaperlessEob,
    isDontAskAgainUpdating,
  } = usePaperlessEob(userInfo?.memberId);
  const paperlessEobUiLabels = queryClient.getQueryData<IUiLabelsResponse>([
    QueryKeys.UI_LABELS,
  ])?.data?.paperlessEOB;

  const [showAlertDialog, setShowAlertDialog] = useState(
    !getItem(STORAGE_KEY.ALERT_DIALOG_STATUS, STORAGE.SESSION)?.find(
      (alertItem: AppAlertDialogStatus) =>
        alertItem?.module === APP_ALERT_MODULE.PAPERLESS_EOB,
    )?.remindLater,
  );

  function handlePaperlessEob() {
    const updateReqData = {
      groupId: userInfo.groupId || "",
      memberId: userInfo.memberId || "",
      accessToken: userInfo.accessToken || "",
      action: PAPARLESS_EOB_UPDATE_ACTION.PAPERLESS,
    };

    updatePaperlessEob(updateReqData, {
      onSuccess: (res: PaperlessEobFetchResponse) => {
        if (!res.data.success) {
          setSuccessAlert({
            ...paperlessEobMessage,
            title: UDPATE_PAPERLESS_EOB_MESSAGE.PAPERLESS.FAILURE.TITLE,
            body: UDPATE_PAPERLESS_EOB_MESSAGE.PAPERLESS.FAILURE.MESSAGE,
            confirmBtnStatus: true,
            icon: CircleX,
            iconClassName: "text-red-700 bg-red-100",
          });
        } else {
          queryClient.setQueryData<PaperlessEobFetchResponse>(
            [QueryKeys.PAPERLESS_EOB_FETCH],
            (prev) =>
              prev
                ? {
                    ...prev,
                    data: { ...prev.data, preference: res?.data?.preference },
                  }
                : res,
          );
          setSuccessAlert({
            ...paperlessEobMessage,
            title: UDPATE_PAPERLESS_EOB_MESSAGE.PAPERLESS.SUCCESS.TITLE,
            body: UDPATE_PAPERLESS_EOB_MESSAGE.PAPERLESS.SUCCESS.MESSAGE,
            confirmBtnStatus: true,
            icon: CircleCheck,
            iconClassName: "text-blue-400 bg-blue-100",
          });
        }
        setItem(
          STORAGE_KEY.ALERT_DIALOG_STATUS,
          [{ module: APP_ALERT_MODULE.PAPERLESS_EOB, remindLater: true }],
          STORAGE.SESSION,
        );
      },
      onError: () => {
        setSuccessAlert({
          ...paperlessEobMessage,
          title: UDPATE_PAPERLESS_EOB_MESSAGE.GENERIC_FAILURE.TITLE,
          body: UDPATE_PAPERLESS_EOB_MESSAGE.GENERIC_FAILURE.MESSAGE,
          confirmBtnStatus: true,
          icon: CircleAlert,
          iconClassName: "text-red-700 bg-red-100",
        });
      },
    });
  }

  function handleRemindLater() {
    setItem(
      STORAGE_KEY.ALERT_DIALOG_STATUS,
      [{ module: APP_ALERT_MODULE.PAPERLESS_EOB, remindLater: true }],
      STORAGE.SESSION,
    );
    setShowAlertDialog(false);
    // dispatch(dequeueAppAlertDialog());
  }

  function onDonotAskAgain() {
    const updateReqData = {
      groupId: userInfo.groupId || "",
      memberId: userInfo.memberId || "",
      accessToken: userInfo.accessToken || "",
      action: PAPARLESS_EOB_UPDATE_ACTION.DONT_ASK_AGAIN,
    };

    dontAskAgainPaperlessEob(updateReqData, {
      onSuccess: (res: PaperlessEobFetchResponse) => {
        if (!res.data.success) {
          setSuccessAlert({
            ...paperlessEobMessage,
            title: UDPATE_PAPERLESS_EOB_MESSAGE.DONT_ASK_AGAIN.FAILURE.TITLE,
            body: UDPATE_PAPERLESS_EOB_MESSAGE.DONT_ASK_AGAIN.FAILURE.MESSAGE,
            confirmBtnStatus: true,
            icon: CircleX,
            iconClassName: "text-red-700 bg-red-100",
          });
        } else {
          setSuccessAlert({
            ...paperlessEobMessage,
            title: UDPATE_PAPERLESS_EOB_MESSAGE.DONT_ASK_AGAIN.SUCCESS.TITLE,
            body: UDPATE_PAPERLESS_EOB_MESSAGE.DONT_ASK_AGAIN.SUCCESS.MESSAGE,
            confirmBtnStatus: true,
            icon: CircleCheck,
            iconClassName: "text-blue-400 bg-blue-100",
          });
        }
        setItem(
          STORAGE_KEY.ALERT_DIALOG_STATUS,
          [{ module: APP_ALERT_MODULE.PAPERLESS_EOB, remindLater: true }],
          STORAGE.SESSION,
        );
      },
      onError: () => {
        setSuccessAlert({
          ...paperlessEobMessage,
          title: UDPATE_PAPERLESS_EOB_MESSAGE.GENERIC_FAILURE.TITLE,
          body: UDPATE_PAPERLESS_EOB_MESSAGE.GENERIC_FAILURE.MESSAGE,
          confirmBtnStatus: true,
          icon: CircleAlert,
          iconClassName: "text-red-700 bg-red-100",
        });
      },
    });
  }

  function closeAlertDialog() {
    setShowAlertDialog(false);
  }

  return (
    <AlertDialog
      open={showAlertDialog}
      logTag={LogTags.MODULE_ACCESS}
      logCategory={LogCategories.PAPERLESS_EOB}
      logSubCategory={LogSubCategories.SWITCH_TO_PAPERLESS_EOB}
      logDialogName={LogSubCategories.SWITCH_TO_PAPERLESS_EOB}
    >
      {successAlert?.confirmBtnStatus ? (
        <PaperlessEobResultDialog
          message={successAlert}
          onClose={closeAlertDialog}
          testId={IDENTIFIERS.PAPERLESS_EOB.ACKNOWLEDGE_BUTTON}
        />
      ) : (
        <AlertDialogContent className="flex min-h-[200px] max-w-[350px] flex-col justify-between gap-4 rounded-lg sm:max-w-lg">
          <AlertDialogHeader className="items-center">
            <AlertDialogTitle className="flex flex-col items-start gap-1 text-lg font-medium">
              {paperlessEobUiLabels?.title ||
                DEFAULT_PAPERLESS_EOB_VALUES.TITLE}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription className="items-center text-center">
            {paperlessEobUiLabels?.body || DEFAULT_PAPERLESS_EOB_VALUES.BODY}
          </AlertDialogDescription>

          <AlertDialogFooter className="mt-2 w-full gap-2 rounded-lg px-2 sm:grid sm:grid-cols-3">
            <Button
              variant="ghost"
              type="button"
              onClick={handleRemindLater}
              className="flex h-fit w-full gap-4 rounded-xl py-4 text-red-800 outline-none hover:text-red-900 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:flex-col sm:gap-2 [&_svg]:size-6"
              disabled={isGoPaperlessUpdating || isDontAskAgainUpdating}
              data-testid={IDENTIFIERS.PAPERLESS_EOB.DISMISS_BUTTON}
            >
              <Bell className="size-full" strokeWidth={1.2} />

              {paperlessEobUiLabels?.action_button?.cancel ||
                DEFAULT_PAPERLESS_EOB_VALUES.DISMISS_BTN}
            </Button>

            <Button
              variant="ghost"
              type="button"
              onClick={onDonotAskAgain}
              className={`relative h-fit w-full rounded-xl py-4 text-gray-600 outline-none hover:text-gray-800 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&_svg]:size-6 ${isDontAskAgainUpdating ? "outline outline-1" : ""}`}
              disabled={
                isDontAskAgainUpdating || isGoPaperlessUpdating || onGuestMode
              }
              data-testid={IDENTIFIERS.PAPERLESS_EOB.DONOT_ASK_AGAIN_BUTTON}
              logMetadata={{
                action: PAPARLESS_EOB_UPDATE_ACTION.DONT_ASK_AGAIN,
              }}
            >
              <div
                className={`flex items-center gap-4 sm:flex-col sm:gap-2 ${isDontAskAgainUpdating ? "invisible" : ""}`}
              >
                <Ban className="size-full" strokeWidth={1.2} />
                <p>
                  {paperlessEobUiLabels?.action_button?.do_not_ask_again ||
                    DEFAULT_PAPERLESS_EOB_VALUES.DONOT_AST_AGAIN_BTN}
                </p>
              </div>
              {isDontAskAgainUpdating && (
                <Loader2 className="absolute inset-0 m-auto size-4 animate-spin" />
              )}
            </Button>

            <Button
              variant="default"
              type="button"
              onClick={handlePaperlessEob}
              className="relative h-fit w-full rounded-xl py-4 outline-none hover:bg-primary-selected focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&_svg]:size-6"
              disabled={
                isGoPaperlessUpdating || isDontAskAgainUpdating || onGuestMode
              }
              data-testid={IDENTIFIERS.PAPERLESS_EOB.CONFIRM_BUTTON}
              logMetadata={{
                action: PAPARLESS_EOB_UPDATE_ACTION.PAPERLESS,
              }}
            >
              <div
                className={`flex items-center gap-4 sm:flex-col sm:gap-2 ${isGoPaperlessUpdating ? "invisible" : ""}`}
              >
                <Leaf className="size-full" strokeWidth={1.2} />
                <p>
                  {paperlessEobUiLabels?.action_button?.confirm ||
                    DEFAULT_PAPERLESS_EOB_VALUES.CONFIRM_BTN}
                </p>
              </div>
              {isGoPaperlessUpdating && (
                <Loader2 className="absolute inset-0 m-auto size-4 animate-spin" />
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      )}
    </AlertDialog>
  );
};

export default PaperlessEobAlert;
